import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as Sentry from '@sentry/react';

import { InterviewType } from '@common/ts/enums';
import { getErrorMessage } from '@common/utils/getErrorDetails';

import { ERROR_MESSAGES } from '@common/constants';
import useChatStore from '../store';
import { iframeLocalStorage } from '../utils/iframeLocalStorage';
import { getLanguageCode } from '../utils/getLanguageCode';
import useChatSettingsStore from '../ChatSettingsStore';
import { translations } from '../translations/chatInputTranslations';
import { getRespondentUiTranslation } from '../translations/respondentUiTranslations';
import { NetworkType, TimeoutProfile } from '../enums';
import { AbortTimeoutConfig } from '../types';
import { SSEParser } from '../utils/sseParser';

interface TSendMessageProps {
  file?: Blob | null;
  text: string;
  onError: (error: Error) => void;
  onSuccess?: () => void;
}

const useServerMessageHandler = (projectStorageSuffix?: string) => {
  const {
    setSeen,
    setReceived,
    setInputsDisabled,
    setStatusOnLastMessage,
    updateLastMessage,
    refreshChatHistory,
    refreshTimeout,
    refreshMessage,
  } = useChatStore();
  const { chatSettingsData } = useChatSettingsStore();
  const isTextChat = chatSettingsData?.includes(InterviewType.TEXT);

  const messageHandler = async (messageData: TResponseHandlerProps, timeout: NodeJS.Timeout) => {
    const {
      data: { message },
      event,
    } = messageData;

    clearTimeout(timeout);
    if (event === 'SEEN') {
      return setSeen();
    }
    if (event === 'STATUS') {
      return setStatusOnLastMessage(message);
    }
    if (event === 'TRANSCODING_VIDEO') {
      const storageKey = `current_project_language_code${projectStorageSuffix}`;
      const lang = storageKey ? iframeLocalStorage.getItem(storageKey) || 'en' : 'en';
      const t = translations[lang] || translations.en;
      return setStatusOnLastMessage(t.processingVideo);
    }
    if (event === 'FILE_URL') {
      // TELV1-554 — the optimistic video bubble previews the local blob of
      // the ORIGINAL upload, which the uploader's browser may be unable to
      // decode (e.g. an HEVC .mov on Firefox without hardware HEVC). The
      // worker emits FILE_URL with the stored file's signed URL — for video
      // that is the transcoded H.264 MP4 — right after the S3 upload, so
      // swap the preview to it and release the blob. Video only: image
      // blobs always render, and swapping them would just refetch.
      const store = useChatStore.getState();
      const messages = [...store.data.messages];
      // Scan back to the newest user message still previewing a blob video —
      // not just the newest user message: a text answer sent after a slow
      // upload (timeout-recovery re-enables inputs) must not shadow the
      // bubble this FILE_URL belongs to. Older video bubbles were already
      // swapped by their own FILE_URL, so they can't match spuriously.
      for (let i = messages.length - 1; i >= 0; i -= 1) {
        const msg = messages[i];
        if (msg.role === 'user' && Array.isArray(msg.data)) {
          const blobItem = msg.data.find(
            (item: { type?: string; url?: string }) =>
              item?.type === InterviewType.VIDEO &&
              typeof item.url === 'string' &&
              item.url.startsWith('blob:'),
          );
          if (blobItem && typeof message === 'string' && message) {
            messages[i] = {
              ...msg,
              data: msg.data.map((item: { url?: string }) =>
                item === blobItem ? { ...item, url: message } : item,
              ),
            };
            useChatStore.setState({ data: { ...store.data, messages } });
            URL.revokeObjectURL(blobItem.url as string);
            break;
          }
        }
      }
      return null;
    }
    if (event === 'MESSAGE') {
      updateLastMessage(message);
      iframeLocalStorage.removeItem('last_user_message');
      // TELV1-480 — an accepted answer resets the no-speech retry counter
      useChatStore.getState().resetNoSpeechAttempts();
      return setInputsDisabled(false);
    }
    if (event === 'MESSAGE_RECEIVED') {
      return setReceived();
    }
    if (event === 'TIMEOUT') {
      await refreshChatHistory?.();
      const ui = getRespondentUiTranslation(getLanguageCode(projectStorageSuffix));
      toast.error(ui.messageProcessingError, {
        autoClose: 10000,
      });
      return refreshTimeout();
    }
    if (event === 'NO_SPEECH_DETECTED') {
      // TELV1-480 — the backend no longer saves anything for a rejected
      // voice attempt (no message, no AI reply, uploaded audio deleted), so
      // the optimistic user bubble has no server counterpart: keeping it
      // would leave a phantom message that vanishes on the next history
      // refresh. Remove it (and its loading assistant bubble) cleanly and
      // let the respondent retry the same question.
      const ui = getRespondentUiTranslation(getLanguageCode(projectStorageSuffix));
      const store = useChatStore.getState();
      const attempts = store.incrementNoSpeechAttempts();

      const messages = [...store.data.messages];
      if (
        messages.length > 0 &&
        messages[messages.length - 1].role === 'assistant' &&
        messages[messages.length - 1].loading
      ) {
        messages.pop();
      }
      if (messages.length > 0 && messages[messages.length - 1].role === 'user') {
        const removed = messages.pop();
        // the optimistic bubble holds a local blob URL for the recording —
        // release it so nothing points at the (deleted) upload
        removed?.data?.forEach((item: { url?: string }) => {
          if (typeof item?.url === 'string' && item.url.startsWith('blob:')) {
            URL.revokeObjectURL(item.url);
          }
        });
      }
      useChatStore.setState({ data: { ...store.data, messages } });

      // After 2 consecutive failed voice attempts, auto-switch to the
      // keyboard input — but ONLY when the project supports text answers:
      // ChatInput renders no text field for voice-only projects, so
      // escalating there would hide the mic bar and promise a keyboard
      // that doesn't exist. Voice-only respondents keep the retry copy.
      // No new translatable strings.
      if (isTextChat && attempts >= 2) {
        store.setShowVoiceMainInput(false);
      }
      const toastMessage = isTextChat
        ? ui.microphoneProblemTextChat
        : ui.microphoneProblemVoiceChat;

      toast.error(toastMessage, {
        autoClose: 10000,
      });

      setInputsDisabled(false);
      // eslint-disable-next-line consistent-return
      return;
    }
    if (event === 'ERROR') {
      refreshMessage(message);
      return setInputsDisabled(false);
    }
    return null;
  };

  return messageHandler;
};

const useSendMessage = (projectStorageSuffix?: string) => {
  const messageHandler = useServerMessageHandler(projectStorageSuffix);
  const { refreshChatHistory, setInputsDisabled } = useChatStore((state) => state);

  const TIMEOUTS = {
    main: {
      [TimeoutProfile.MobileLarge]: 60000,
      [TimeoutProfile.Mobile]: 40000,
      [TimeoutProfile.Desktop]: 30000,
    },
    initial: {
      [TimeoutProfile.MobileLarge]: 30000,
      [TimeoutProfile.Mobile]: 20000,
      [TimeoutProfile.Desktop]: 15000,
    },
  };

  const getTimeoutProfile = (isMobile: boolean, hasLargeFile: boolean): TimeoutProfile => {
    if (isMobile && hasLargeFile) return TimeoutProfile.MobileLarge;
    if (isMobile) return TimeoutProfile.Mobile;
    return TimeoutProfile.Desktop;
  };

  const calculateTimeoutDurations = (isMobile: boolean, hasLargeFile: boolean) => {
    const profile = getTimeoutProfile(isMobile, hasLargeFile);
    return {
      mainTimeoutDuration: TIMEOUTS.main[profile],
      initialTimeoutDuration: TIMEOUTS.initial[profile],
    };
  };

  // Base timeout covers text-only messages + AI response generation.
  // File uploads and slow networks add on top via adjustments below.
  const BASE_ABORT_TIMEOUT = 90000;
  const FileSizeAdjustments = {
    Mobile: {
      perMB: 15000,
      max: 300000,
    },
    Desktop: {
      perMB: 10000,
      max: 240000,
    },
  };

  const NetworkAdjustments: Record<NetworkType, number> = {
    [NetworkType._2G]: 120000,
    [NetworkType._3G]: 60000,
    [NetworkType._4G]: 30000,
    [NetworkType.Unknown]: 15000,
  };

  // Utility to determine file size adjustment
  const calculateFileSizeAdjustment = (
    file: Blob | null | undefined,
    isMobile: boolean,
  ): number => {
    if (!file) return 0;
    const profile = isMobile ? 'Mobile' : 'Desktop';
    const { perMB, max } = FileSizeAdjustments[profile];
    const fileSizeMB = file.size / (1024 * 1024);
    return Math.min(fileSizeMB * perMB, max);
  };

  // Utility to determine network adjustment
  const calculateNetworkAdjustment = (): number => {
    const { connection } = navigator as Navigator & { connection?: { effectiveType?: string } };
    if (!connection) return 0;
    const effectiveType = (connection.effectiveType as NetworkType) || NetworkType.Unknown;
    return NetworkAdjustments[effectiveType] ?? 0;
  };

  // Main timeout calculation
  const getAbortTimeoutDuration = ({ file, isMobile }: AbortTimeoutConfig): number => {
    return (
      BASE_ABORT_TIMEOUT +
      calculateFileSizeAdjustment(file, isMobile) +
      calculateNetworkAdjustment()
    );
  };

  const handleHistoryError = (error: AxiosError) => {
    const message = getErrorMessage(error);
    toast.error(message);
  };

  const sendMessage = async ({ file, text, onError, onSuccess }: TSendMessageProps) => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    let thisRequestReceivedResponse = false;
    let initialResponseTimeout: NodeJS.Timeout | null = null;
    // TELV1-547 review: hoisted so EVERY recovery path can clear it — a
    // dangling abort timer now carries user-visible side effects.
    let abortTimeout: ReturnType<typeof setTimeout> | null = null;
    const hasLargeFile = !!(file && file.size > 1024 * 1024 * 5); // 5MB

    const { mainTimeoutDuration, initialTimeoutDuration } = calculateTimeoutDurations(
      isMobile,
      hasLargeFile,
    );

    // Apply network and file-size adjustments to all timeouts, not just abortTimeout
    const networkAdjustment = calculateNetworkAdjustment();
    const fileSizeAdjustment = calculateFileSizeAdjustment(file, isMobile);
    const adjustedMainTimeout = mainTimeoutDuration + networkAdjustment + fileSizeAdjustment;
    const adjustedInitialTimeout = initialTimeoutDuration + networkAdjustment + fileSizeAdjustment;

    const controller = new AbortController();

    const timeoutId = setTimeout(async () => {
      if (initialResponseTimeout) clearTimeout(initialResponseTimeout);
      if (abortTimeout) clearTimeout(abortTimeout);
      controller.abort();
      setInputsDisabled(false);
      toast.error(
        getRespondentUiTranslation(getLanguageCode(projectStorageSuffix)).responseTimeoutError,
      );

      try {
        await refreshChatHistory?.();
      } catch (error) {
        handleHistoryError(error);
      }
    }, adjustedMainTimeout);

    const formdata = new FormData();
    if (file) {
      formdata.append('file', file);
    }
    formdata.append('text', text);

    try {
      // Calculate dynamic timeout based on file size and network conditions
      const timeoutDuration = getAbortTimeoutDuration({
        file,
        isMobile,
      });

      abortTimeout = setTimeout(async () => {
        // TELV1-547: this was a bare controller.abort() with no cleanup —
        // once the first SSE event had cleared the other timers, an abort
        // here left the respondent on a permanent spinner with inputs
        // disabled forever. Recover like the other timeout paths do.
        controller.abort();
        clearTimeout(timeoutId);
        if (initialResponseTimeout) clearTimeout(initialResponseTimeout);
        setInputsDisabled(false);
        toast.error(
          getRespondentUiTranslation(getLanguageCode(projectStorageSuffix)).responseTimeoutError,
        );
        try {
          await refreshChatHistory?.();
        } catch (error) {
          handleHistoryError(error as AxiosError);
        }
      }, timeoutDuration);

      const conversationId = useChatStore.getState().currentConversationId;
      const doFetch = () =>
        fetch(`${import.meta.env.VITE_CHAT_URL}/chat/${conversationId}`, {
          method: 'PUT',
          redirect: 'follow',
          body: formdata,
          signal: controller.signal,
        });
      let sse = await doFetch();

      // TELV1-547 — the worker answers 503 + Retry-After during graceful
      // shutdown (deploys), explicitly asking the client to retry; honor it
      // with a bounded wait so a respondent mid-answer during a deploy does
      // not lose their take.
      // Large media re-uploads on every retry — cap those to one attempt.
      const maxDeployRetries = file && file.size > 2 * 1024 * 1024 ? 1 : 2;
      let deployRetries = 0;
      while (sse.status === 503 && deployRetries < maxDeployRetries) {
        const retryAfterHeader = Number(sse.headers.get('Retry-After'));
        const delaySeconds =
          Number.isFinite(retryAfterHeader) && retryAfterHeader > 0
            ? Math.min(retryAfterHeader, 15)
            : 4;
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => {
          setTimeout(resolve, delaySeconds * 1000);
        });
        // A main/abort timeout may have fired during the wait and already
        // recovered the UI — do not race it with another request.
        if (controller.signal.aborted) {
          // Whichever timer aborted has already run its own recovery (both
          // clear the other timers) — these clears are pure belt-and-braces
          // so no timer can outlive this early return (PR review).
          clearTimeout(timeoutId);
          if (abortTimeout) clearTimeout(abortTimeout);
          if (initialResponseTimeout) clearTimeout(initialResponseTimeout);
          return;
        }
        deployRetries += 1;
        // eslint-disable-next-line no-await-in-loop
        sse = await doFetch();
      }

      // TELV1-547 — an HTTP error response still has a readable body, so
      // the SSE reader hit `done` with zero events, CLEARED every recovery
      // timer and returned: permanent spinner, inputs disabled, take lost,
      // no message. Fail loudly instead: toast, re-enable, resync history.
      if (!sse.ok) {
        clearTimeout(timeoutId);
        if (abortTimeout) clearTimeout(abortTimeout);
        setInputsDisabled(false);
        toast.error(
          getRespondentUiTranslation(getLanguageCode(projectStorageSuffix)).noResponseError,
        );
        try {
          await refreshChatHistory?.();
        } catch (error) {
          handleHistoryError(error as AxiosError);
        }
        return;
      }

      // Start initialResponseTimeout only after upload completes (response headers arrived).
      // Set on every request (not guarded by hasReceivedFirstResponse) so subsequent
      // messages also benefit from the fast-fail timeout.
      initialResponseTimeout = setTimeout(async () => {
        if (!thisRequestReceivedResponse) {
          clearTimeout(timeoutId);
          if (abortTimeout) clearTimeout(abortTimeout);
          controller.abort();
          setInputsDisabled(false);
          toast.error(
            getRespondentUiTranslation(getLanguageCode(projectStorageSuffix)).noResponseError,
          );
          try {
            await refreshChatHistory?.();
          } catch (error) {
            handleHistoryError(error);
          }
        }
      }, adjustedInitialTimeout);

      if (sse.body && sse.body.getReader) {
        const reader = sse.body.getReader();
        const sseParser = new SSEParser();

        const readChunk = async () => {
          try {
            const { done, value } = await reader.read();
            if (done) {
              sseParser.reset();
              reader.releaseLock();
              clearTimeout(timeoutId);
              if (initialResponseTimeout) clearTimeout(initialResponseTimeout);
              if (abortTimeout) clearTimeout(abortTimeout);
              return;
            }

            const messages = sseParser.parseChunk(value);
            messages.forEach((parsedMessage) => {
              thisRequestReceivedResponse = true;
              if (initialResponseTimeout) clearTimeout(initialResponseTimeout);
              if ((parsedMessage as TResponseHandlerProps).event === 'MESSAGE') {
                onSuccess?.();
              }
              messageHandler(parsedMessage as TResponseHandlerProps, timeoutId);
            });

            readChunk();
          } catch (readError) {
            sseParser.reset();
            if (readError instanceof DOMException && readError.name === 'AbortError') {
              return;
            }
            console.error('Error reading from stream:', readError);
            reader.releaseLock();
            if (abortTimeout) clearTimeout(abortTimeout);
            if (initialResponseTimeout) clearTimeout(initialResponseTimeout);

            // Auto-recovery: fetch latest state from MongoDB, then check if
            // the AI already completed its response server-side.
            try {
              await refreshChatHistory?.();
            } catch (error) {
              handleHistoryError(error);
            }

            const { messages } = useChatStore.getState().data;
            const lastMsg = messages[messages.length - 1];
            const aiAlreadyResponded = lastMsg?.role === 'assistant' && !lastMsg.loading;

            if (!aiAlreadyResponded) {
              toast.info(
                getRespondentUiTranslation(getLanguageCode(projectStorageSuffix))
                  .connectionInterrupted,
                { autoClose: 8000 },
              );
            }
            setInputsDisabled(false);
          }
        };

        readChunk();
      }
    } catch (error) {
      clearTimeout(timeoutId);
      if (abortTimeout) clearTimeout(abortTimeout);
      if (initialResponseTimeout) clearTimeout(initialResponseTimeout);
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }
      Sentry.captureException(error);
      onError(error as AxiosError);
    }
  };

  const onSendMessageErrorHandler = (error: unknown) => {
    const axiosError = error as AxiosError;
    let message = getErrorMessage(axiosError);

    // Check for TypeError: Failed to fetch
    if (error instanceof TypeError && error.message === ERROR_MESSAGES.FAILED_TO_FETCH) {
      message = ERROR_MESSAGES.FAILED_TO_SEND_MESSAGE_WHILE_NETWORK_ERROR;
    }

    toast.error(message);
    setInputsDisabled(false);
  };

  return { sendMessage, onSendMessageErrorHandler };
};

export default useSendMessage;
